import { formatters } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import { useEffect, useState, type FC } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { SmallDisclosure } from 'src/libs/SmallDisclosure/SmallDisclosure';

import { VerifiedBySeekPassIcon } from '../../VerifiedBySeekPassIcon';
import { ProfileDetails } from '../ProfileDetails';
import { TextDisclosureWithLinks } from '../TextDisclosureWithLinks';

import translations from './.vocab';

interface EducationMonthYear {
  month?: number;
  year: number;
}

const useGetFormattedCompletionDate = ({
  completed,
  completionDate,
}: {
  completed: boolean;
  completionDate?: EducationMonthYear;
}) => {
  const [formattedCompletion, setFormattedCompletion] = useState('');
  const { language, locale } = useAppConfig();

  useEffect(() => {
    let shouldSetFormattedCompletion = true;
    formatters
      .formatEducationCompletion(
        { completed, completionDate },
        language as 'en' | 'id' | 'th',
        locale,
      )
      .then((result) => {
        if (shouldSetFormattedCompletion) {
          setFormattedCompletion(result);
        }
      });
    return () => {
      shouldSetFormattedCompletion = false;
    };
  }, [completed, completionDate, language, locale]);

  return formattedCompletion;
};

interface EducationProps {
  id: string;
  title: string;
  institute: string;
  highlights?: string;
  completed: boolean;
  completionDate?: EducationMonthYear;
  verificationViewFields?: Array<{ name: string; values: string[] }>;
}

export const Education: FC<EducationProps> = ({
  id,
  title,
  institute,
  completed,
  completionDate,
  highlights,
  verificationViewFields,
}) => {
  const { t } = useTranslations(translations);
  const formattedCompletionDate = useGetFormattedCompletionDate({
    completed,
    completionDate,
  });

  const isVerified =
    verificationViewFields && verificationViewFields?.length > 0;

  return (
    <Stack space="small">
      {isVerified ? (
        <VerifiedBySeekPassIcon id={id} title={title} />
      ) : (
        <Text weight="strong">{title}</Text>
      )}

      <Text size="small">{institute}</Text>
      <Text size="small" tone="secondary">
        {formattedCompletionDate}
      </Text>
      {highlights ? (
        <TextDisclosureWithLinks
          linesToPreview={3}
          text={highlights}
          size="small"
        />
      ) : null}
      {isVerified ? (
        <SmallDisclosure
          id={id}
          expandLabel={t('Show verification details')}
          collapseLabel={t('Hide verification details')}
          weight="weak"
        >
          <ProfileDetails details={verificationViewFields} />
        </SmallDisclosure>
      ) : null}
    </Stack>
  );
};
